import {
  BookingV4,
  CustomerPreorderSettings,
  CustomerPreorderMenu
} from "../../types/bookings";
import { CollinsFeatures } from "../../types/VenueGroup";
import { postUpdateBookingV4 } from "./UpdateBooking";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { getBuildVersion } from "../getDefaultHeaders";

/**
 * Get the customer pre-order checkout URL for a booking
 * @param bookingId - The booking ID
 * @param isLeadBooker - Whether the URL is for the lead booker or group member
 * @param collinsFeatures - Optional collins features to determine checkout experience
 * @returns Promise resolving to the checkout URL
 */
export const getCustomerPreOrderUrl = async (
  bookingId: string,
  isLeadBooker: boolean,
  collinsFeatures?: CollinsFeatures
): Promise<string> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const newCheckoutUrl = environmentVars?.NEW_CHECKOUT_URL ?? "";
  const baseBookingsUrl = environmentVars?.BASE_BOOKINGS_URL ?? "";

  const path = isLeadBooker ? "/manage-your-order" : "/place-your-order";

  const hasNewGuestExperience = collinsFeatures?.new_guest_experience;

  if (hasNewGuestExperience) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(
        `${environmentVars?.COLLINS_API_BASE_URL}/signature`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Build-Version": getBuildVersion()
          },
          body: JSON.stringify({ bookingId }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const signature = data?.signature ?? "";
        if (signature && newCheckoutUrl) {
          return `${newCheckoutUrl}${path}?booking_id=${bookingId}&signature=${signature}`;
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        console.error("Signature endpoint request timed out after 10 seconds");
      } else {
        console.error("Error generating signature for pre-order URL:", error);
      }
    }

    return `${newCheckoutUrl}${path}?booking_id=${bookingId}`;
  }

  return `${baseBookingsUrl}${path}?booking_id=${bookingId}`;
};

/**
 * Add a menu to the customer preorder settings
 * @param booking - The booking to update
 * @param menuId - The menu ID to add
 * @param venuePreordersCloseDays - Optional number of days before booking to close preorders
 * @returns Promise resolving to the updated booking or error
 */
export const addCustomerPreorderMenu = async (
  booking: BookingV4,
  menuId: string,
  venuePreordersCloseDays?: number
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const existingSettings = booking.customer_preorder_settings ?? {
      complete: false,
      menus: []
    };

    const existingMenus = existingSettings.menus ?? [];

    const menuAlreadyExists = existingMenus.some(
      (menu) => menu.menu_id === menuId
    );
    if (menuAlreadyExists) {
      return { error: "Menu already added to this booking" };
    }

    const newMenu: CustomerPreorderMenu = {
      menu_id: menuId
    };

    let closeDate = existingSettings.close_date;
    if (!closeDate && booking.date) {
      const bookingDate = new Date(booking.date);
      const closeDays = venuePreordersCloseDays ?? 5;
      bookingDate.setDate(bookingDate.getDate() - closeDays);

      const today = new Date();
      if (bookingDate > today) {
        closeDate = bookingDate;
      }
    }

    const updatedSettings: CustomerPreorderSettings = {
      ...existingSettings,
      menus: [...existingMenus, newMenu],
      close_date: closeDate
    };

    const updatedBooking: BookingV4 = {
      ...booking,
      customer_preorder_settings: updatedSettings,
      // Strip preorders to prevent the Core API from re-processing them (see AB#2210242)
      preorders: undefined
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to add menu to customer preorder settings" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error adding customer preorder menu:", error);
    return { error: "Failed to add menu to customer preorder settings" };
  }
};

/**
 * Remove a menu from the customer preorder settings
 * @param booking - The booking to update
 * @param menuIndex - The index of the menu to remove
 * @returns Promise resolving to the updated booking or error
 */
export const removeCustomerPreorderMenu = async (
  booking: BookingV4,
  menuIndex: number
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const existingSettings = booking.customer_preorder_settings;

    if (
      !existingSettings?.menus ||
      menuIndex >= existingSettings.menus.length
    ) {
      return { error: "Menu not found" };
    }

    const updatedMenus = [...existingSettings.menus];
    updatedMenus.splice(menuIndex, 1);

    const updatedSettings: CustomerPreorderSettings = {
      ...existingSettings,
      menus: updatedMenus
    };

    const updatedBooking: BookingV4 = {
      ...booking,
      customer_preorder_settings: updatedSettings,
      // Strip preorders to prevent the Core API from re-processing them (see AB#2210242)
      preorders: undefined
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to remove menu from customer preorder settings" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error removing customer preorder menu:", error);
    return { error: "Failed to remove menu from customer preorder settings" };
  }
};

/**
 * Update the customer preorder settings for a booking
 * @param booking - The booking to update
 * @param settings - Partial settings to update
 * @returns Promise resolving to the updated booking or error
 */
export const updateCustomerPreorderSettings = async (
  booking: BookingV4,
  settings: Partial<CustomerPreorderSettings>
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const existingSettings = booking.customer_preorder_settings ?? {
      complete: false,
      menus: []
    };

    const updatedSettings: CustomerPreorderSettings = {
      ...existingSettings,
      ...settings
    };

    const updatedBooking: BookingV4 = {
      ...booking,
      customer_preorder_settings: updatedSettings,
      // Strip preorders to prevent the Core API from re-processing them (see AB#2210242)
      preorders: undefined
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to update customer preorder settings" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error updating customer preorder settings:", error);
    return { error: "Failed to update customer preorder settings" };
  }
};

/**
 * Update a specific menu in the customer preorder settings
 * @param booking - The booking to update
 * @param menuIndex - The index of the menu to update
 * @param menuSettings - Partial menu settings to update
 * @returns Promise resolving to the updated booking or error
 */
export const updateCustomerPreorderMenu = async (
  booking: BookingV4,
  menuIndex: number,
  menuSettings: Partial<CustomerPreorderMenu>
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const existingSettings = booking.customer_preorder_settings;

    if (
      !existingSettings?.menus ||
      menuIndex >= existingSettings.menus.length
    ) {
      return { error: "Menu not found" };
    }

    const updatedMenus = [...existingSettings.menus];
    updatedMenus[menuIndex] = {
      ...updatedMenus[menuIndex],
      ...menuSettings
    };

    const updatedSettings: CustomerPreorderSettings = {
      ...existingSettings,
      menus: updatedMenus
    };

    const updatedBooking: BookingV4 = {
      ...booking,
      customer_preorder_settings: updatedSettings,
      // Strip preorders to prevent the Core API from re-processing them (see AB#2210242)
      preorders: undefined
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to update customer preorder menu" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error updating customer preorder menu:", error);
    return { error: "Failed to update customer preorder menu" };
  }
};

/**
 * Toggle the complete status of customer preorder settings
 * @param booking - The booking to update
 * @returns Promise resolving to the updated booking or error
 */
export const toggleCustomerPreordersComplete = async (
  booking: BookingV4
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const existingSettings = booking.customer_preorder_settings ?? {
      complete: false,
      menus: []
    };

    const isCurrentlyComplete = existingSettings.complete;

    const updatedPreorders = booking.preorders?.map((preorder) => {
      if (preorder.source === "customer" && preorder.status !== "deleted") {
        return {
          ...preorder,
          status: isCurrentlyComplete ? "pending" : "complete"
        };
      }
      return preorder;
    });

    const updatedSettings: CustomerPreorderSettings = {
      ...existingSettings,
      complete: !isCurrentlyComplete
    };

    const updatedBooking: BookingV4 = {
      ...booking,
      customer_preorder_settings: updatedSettings,
      preorders: updatedPreorders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to toggle customer preorders complete status" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error toggling customer preorders complete:", error);
    return { error: "Failed to toggle customer preorders complete status" };
  }
};
